import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BrowseInstructor {
  id: string;
  full_name: string;
  username: string | null;
  profile_picture_url: string | null;
  belt_rank: string | null;
  jiu_jitsu_school: string | null;
  city: string | null;
  country: string | null;
  accept_roll_reviews: boolean;
  has_public_agenda: boolean;
  class_value: number | null;
  class_currency: string | null;
  roll_review_price_cents: number | null;
  roll_review_currency: string | null;
  is_premium: boolean;
}

export interface BrowseInstructorCounts {
  all: number;
  videoReview: number;
  privateClasses: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function prioritize(
  instructors: BrowseInstructor[],
  mySchool: string | null,
  myCity: string | null,
): BrowseInstructor[] {
  return [...instructors].sort((a, b) => {
    // Tier 0: same school  |  Tier 1: same city  |  Tier 2: everyone else
    const tierA = mySchool && a.jiu_jitsu_school === mySchool ? 0
      : myCity && a.city === myCity ? 1
      : 2;
    const tierB = mySchool && b.jiu_jitsu_school === mySchool ? 0
      : myCity && b.city === myCity ? 1
      : 2;
    if (tierA !== tierB) return tierA - tierB;
    // Within the same tier: Pro instructors first
    if (a.is_premium !== b.is_premium) return a.is_premium ? -1 : 1;
    // Within same tier + same Pro status: alphabetical
    return (a.full_name ?? '').localeCompare(b.full_name ?? '');
  });
}

// ─── Geolocation capture ──────────────────────────────────────────────────────

const LOCATION_DISMISSED_KEY = 'sossiety:location_prompt_dismissed';

export function hasLocationPromptBeenDismissed(): boolean {
  try {
    return localStorage.getItem(LOCATION_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissLocationPrompt(): void {
  try {
    localStorage.setItem(LOCATION_DISMISSED_KEY, '1');
  } catch {
    // localStorage unavailable — silent
  }
}

async function captureAndSaveLocation(userId: string): Promise<void> {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
          );
          const data = await res.json();
          const city    = data.city || data.locality || null;
          const state   = data.principalSubdivision || null;
          const country = data.countryName || null;
          if (city || country) {
            await supabase
              .from('profiles')
              .update({ city, state, country })
              .eq('id', userId);
          }
        } catch {
          // reverse-geocode failed — proximity tier simply unavailable
        } finally {
          resolve();
        }
      },
      () => {
        // denied — mark as dismissed so we never prompt again
        dismissLocationPrompt();
        resolve();
      },
      { timeout: 8000 },
    );
  });
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export async function fetchInstructors(currentUserId: string): Promise<BrowseInstructor[]> {
  const { data, error } = await supabase
    .from('v_profiles_with_premium')
    .select(`
      id,
      full_name,
      username,
      profile_picture_url,
      belt_rank,
      jiu_jitsu_school,
      city,
      country,
      accept_roll_reviews,
      is_premium,
      instructor_agenda_config (
        is_public,
        class_value,
        currency
      ),
      roll_review_pricing (
        currency,
        tiers
      )
    `)
    .eq('is_instructor', true)
    .neq('id', currentUserId)
    .order('is_premium', { ascending: false });

  if (error) throw error;

  return (data ?? []).map((row: any) => {
    const config = Array.isArray(row.instructor_agenda_config)
      ? row.instructor_agenda_config[0]
      : row.instructor_agenda_config;

    const rrPricing = Array.isArray(row.roll_review_pricing)
      ? row.roll_review_pricing[0]
      : row.roll_review_pricing;

    // Use tier_1 (lowest: 0–2.5 min) as the "starting from" price for the card
    const tiers: any[] = rrPricing?.tiers ?? [];
    const tier1 = tiers[0];

      return {
      id:                      row.id,
      full_name:               row.full_name,
      username:                row.username ?? null,
      profile_picture_url:     row.profile_picture_url ?? null,
      belt_rank:               row.belt_rank ?? null,
      jiu_jitsu_school:        row.jiu_jitsu_school ?? null,
      city:                    row.city ?? null,
      country:                 row.country ?? null,
      accept_roll_reviews:     row.accept_roll_reviews ?? false,
      has_public_agenda:       config?.is_public === true,
      class_value:             config?.class_value ?? null,
      class_currency:          config?.currency ?? null,
      roll_review_price_cents: tier1?.price_cents > 0 ? tier1.price_cents : null,
      roll_review_currency:    rrPricing?.currency ?? null,
      is_premium:              row.is_premium === true,
    };
  });
}

async function fetchCurrentUserProfile(
  userId: string,
): Promise<{ jiu_jitsu_school: string | null; city: string | null }> {
  const { data, error } = await supabase
    .from('profiles')
    .select('jiu_jitsu_school, city')
    .eq('id', userId)
    .single();
  if (error) return { jiu_jitsu_school: null, city: null };
  return {
    jiu_jitsu_school: data?.jiu_jitsu_school ?? null,
    city:             data?.city ?? null,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBrowseInstructors() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch instructor list
  const {
    data: rawInstructors = [],
    isLoading: instructorsLoading,
    error: instructorsError,
    refetch,
  } = useQuery({
    queryKey:  ['browse-instructors', user?.id],
    enabled:   !!user?.id,
    queryFn:   () => fetchInstructors(user!.id),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch current user profile for prioritization
  const { data: myProfile } = useQuery({
    queryKey:  ['my-profile-brief', user?.id],
    enabled:   !!user?.id,
    queryFn:   () => fetchCurrentUserProfile(user!.id),
    staleTime: 10 * 60 * 1000,
  });

  // Whether to show the contextual location banner
  // Shown only when: profile loaded, city absent, and user hasn't dismissed before
  const showLocationBanner =
    !!user?.id &&
    myProfile !== undefined &&
    !myProfile.city &&
    !hasLocationPromptBeenDismissed();

  const [locationRequested, setLocationRequested] = useState(false);

  // Called when the user taps "Allow" in the banner
  const requestLocation = () => {
    if (!user?.id || locationRequested) return;
    setLocationRequested(true);
    captureAndSaveLocation(user.id).then(() => {
      queryClient.invalidateQueries({ queryKey: ['my-profile-brief', user.id] });
      queryClient.invalidateQueries({ queryKey: ['browse-instructors', user.id] });
    });
  };

  // Reset locationRequested if profile changes (e.g. city was populated)
  useEffect(() => {
    if (myProfile?.city) setLocationRequested(false);
  }, [myProfile?.city]);

  // Prioritized full list (stable, unaffected by search/filter)
  const instructors = useMemo(
    () => prioritize(rawInstructors, myProfile?.jiu_jitsu_school ?? null, myProfile?.city ?? null),
    [rawInstructors, myProfile],
  );

  // Counts based on the full (un-searched) list
  const counts: BrowseInstructorCounts = useMemo(() => ({
    all:            instructors.length,
    videoReview:    instructors.filter(i => i.accept_roll_reviews && i.is_premium).length,
    privateClasses: instructors.filter(i => i.has_public_agenda).length,
  }), [instructors]);

  return {
    instructors,
    counts,
    isLoading:          instructorsLoading,
    error:              instructorsError,
    refetch,
    showLocationBanner,
    locationRequested,
    requestLocation,
    dismissLocation:    dismissLocationPrompt,
  };
}
